<template>
  <div class="event" :class="eventClasses" @click="eventClicked">
    <div class="gridElement h-100">
      <div class="id flex align-items-center header" :class="recentlyChanged">
        <IDBadge
          :id="event.id"
          class="text-center IDBadgeWidth"
          :prefix="idPrefix(event.__typename)"
          @click="buttonsDisabled ? null : $emit('openEditComponent')"
        />
      </div>
      <div class="flex align-items-center header">
        <div class="owner">
          <slot name="owner">
            <UserBadge
              :user="event.owner"
              :event="event"
              class="bounceTag"
              @click="setUserFilter(event.owner, $event)"
            />
          </slot>
        </div>

        <div class="grow">
          <slot name="leadingHeaderAdditions" />
        </div>

        <div
          class="flex align-items-center justify-content-end flags text-right text-truncate gap-1"
        >
          <FlagBadge
            v-for="flag in sortedFlags"
            :key="flag.id"
            class="chipStyleEAB bounceTag cursor-pointer"
            :flag="flag"
            @click="setFlagFilter(flag, $event)"
          >
            <template v-if="ifEventIncludedOverFlag(flag)" #addition>
              <InfoTooltip
                :title="t('components.event-action.issue.AbstractIssue.flagTooltipTitle')"
                class="p-0 m-0"
                :text="
                  t('components.event-action.issue.AbstractIssue.flagTooltipTextBoard', {
                    name: originBoard?.name,
                    id: originBoard?.id,
                  })
                "
              />
            </template>
          </FlagBadge>
          <EisenhowerSelector
            v-model="event.eisenhowerPriority"
            :disabled="readOnly"
            :show-chip="true"
            :update-event-directly="{
              eventId: event.id,
              eventType: event.__typename,
            }"
          />
        </div>
        <div>
          <slot name="trailingHeaderAdditionsFront" />
        </div>
        <div class="deadline">
          <slot name="deadline">
            <DeadlineBadge :deadline="event.occurrenceDate" />
          </slot>
        </div>

        <div class="buttonGroup flex justify-content-between">
          <EventConfigButtonList
            v-if="!buttonsDisabled && canWriteEvent"
            v-bind="eventConfigButtonListProps"
            :event="event"
          />
          <EventActionButtonList
            v-if="!buttonsDisabled && !readOnly"
            ref="eventActionButtonList"
            v-bind="{ ...eventActionButtonListProps, ...$attrs }"
            :event="event"
          />
        </div>

        <slot name="trailingHeaderAdditions" />
      </div>

      <div class="linked flex justify-content-around align-items-start" :class="recentlyChanged">
        <div>
          <div>
            <template v-if="originBoard?.id != null && originBoard?.id !== currentBoardId">
              <div v-if="originBoard?.name == null" class="w-100 text-left">
                {{ t("global.deleted") }}
              </div>
              <span
                v-else
                v-tooltip.bottom="{
                  disabled: hasOriginBoardAccess,
                  value: t('components.kpiBoard.BoardHierarchy.BoardDisabled'),
                }"
                class="inline-block"
              >
                <LISAButton
                  style="width: calc(8ch + 10.5px); font-size: 1em"
                  class="border-0 p-0 m-0"
                  outlined
                  size="small"
                  text
                  :tooltip="
                    hasOriginBoardAccess
                      ? t('components.kpiBoard.BoardHierarchy.BoardDisabled')
                      : undefined
                  "
                  :disabled="!hasOriginBoardAccess"
                  :title="hasOriginBoardAccess ? buttonTitle : undefined"
                  @click="
                    $router.push({
                      name: 'EventActionBoardNew',
                      params: {
                        boardId: originBoard?.id.toString() ?? '',
                      },
                      query: {
                        scrollToEntity: 'event',
                        scrollToType: event.__typename,
                        scrollToId: event.id.toString(),
                      },
                    })
                  "
                >
                  <span class="text-truncate">{{ originBoard?.name }}</span>
                </LISAButton>
              </span>
            </template>
            <div class="flex justify-content-center pb-2">
              <BoardOriginArrow :event="event" />
              <div
                v-if="event.linkedEvents.length > 0"
                class="cursor-pointer inline mr-1"
                @click="setIdFilter"
              >
                <InfoTooltip
                  :text="
                    event.linkedEvents
                      .map((linkedEvent) => `${idPrefix(linkedEvent.__typename)}${linkedEvent.id}`)
                      .join(', ')
                  "
                  class="text-secondary"
                  icon="fas fa-link"
                  :title="t('components.event-action.issue.AbstractIssue.linkedEvents')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="content">
        <slot />
      </div>
    </div>
    <slot name="editComponent" />
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType, inject, ref } from "vue";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import LISAButton from "@LISAPrime/LISAButton.vue";
import { type ArchiveRange, type EventListEventFieldsFragment } from "@/graphql";
import {
  ArchiveRangeKey,
  BoardIdInjectionKey,
  BoardIncludedFlagIdsInjectionKey,
  EventActionButtonsDisabledKey,
  getRecentlyChangedAlternative,
  RecentlyChangedAlternativeEnum,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import IDBadge from "../../badges/IDBadge.vue";
import UserBadge from "../../badges/UserBadge.vue";
import DeadlineBadge from "../../badges/DeadlineBadge.vue";
import { taskCommentEmitter } from "../task/TaskCommentSection.vue";
import {
  IDFilter,
  RecentlyChangedFilter,
  FlagsFilter,
  CreatorFilter,
} from "../filter/filterImplementations";
import BoardOriginArrow from "./BoardOriginArrow.vue";
import { recentTime } from "../helper/EventBoardRecentlyChangedHelper";
import injectAsserted from "@/functions/assert/injectAsserted";
import { idPrefix } from "@/components/event-action/event/EventActionIdPrefix";
import { EventFilterInjectionKey } from "../filter/FilterTypes";
import EventActionButtonList from "./action-buttons/EventActionButtonList.vue";
import EventConfigButtonList from "./action-buttons/EventConfigButtonList.vue";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import { useGetBoardInfo, useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import { hasRight } from "@/functions/authorizationState";
import FlagBadge from "@/components/badges/FlagBadge.vue";
import vTooltip from "primevue/tooltip";
import EisenhowerSelector from "./eisenhowerMatrix/EisenhowerSelector.vue";

const props = defineProps({
  event: {
    type: Object as PropType<EventListEventFieldsFragment>,
    required: true,
  },
  eventConfigButtonListProps: {
    type: Object as PropType<Record<string, any>>,
    required: false,
    default: () => ({}),
  },
  eventActionButtonListProps: {
    type: Object as PropType<Record<string, any>>,
    required: false,
    default: () => ({}),
  },
});

defineEmits(["click", "openEditComponent", "EisenhowerMatrixModal"]);
const { t, te } = useTranslation();
const recentlyChangedAlternativeKey = ref(getRecentlyChangedAlternative());
const eventActionButtonList = ref<InstanceType<typeof EventActionButtonList>>();

const buttonsDisabled = inject(
  EventActionButtonsDisabledKey,
  computed(() => false),
);
const { siteId: boardSiteId } = useProvidedBoardContext();
const canWriteEvent = computed(() => hasRight("sqdc.write", boardSiteId.value));
const readOnly = computed(() => props.event.deletedAt != null || !canWriteEvent.value);

defineExpose({
  eventActionButtonList,
});
const sortedFlags = computed(() => {
  return [...props.event.flags].sort((a, b) => {
    const displayNameA = te(`dynamic.${a.abbreviationId}`)
      ? t(`dynamic.${a.abbreviationId}`)
      : te(`dynamic.${a.displayNameId}`)
        ? t(`dynamic.${a.displayNameId}`)
        : (a.name ?? `[${t("global.id")}:${a.id}]`);

    const displayNameB = te(`dynamic.${b.abbreviationId}`)
      ? t(`dynamic.${b.abbreviationId}`)
      : te(`dynamic.${b.displayNameId}`)
        ? t(`dynamic.${b.displayNameId}`)
        : (b.name ?? `[${t("global.id")}:${b.id}]`);

    return displayNameA.localeCompare(displayNameB);
  });
});

const archiveRange = inject(ArchiveRangeKey, ref<ArchiveRange | undefined>(undefined));
const eventClicked = () => {
  taskCommentEmitter.emit("showNewComment", -1);
};

const recentlyChangedFilter = new RecentlyChangedFilter(recentTime.value);

const recentlyChanged = computed(() => {
  if (!recentlyChangedFilter.isEventActive(props.event, originBoard.value?.id)) {
    return {};
  }
  switch (recentlyChangedAlternativeKey.value) {
    case RecentlyChangedAlternativeEnum.NORMAL:
      return { recentlyChanged: true };
    case RecentlyChangedAlternativeEnum.GREEN_WAVES:
      return { recentlyChangedAlternative: true };
    default:
      return undefined;
  }
});

const eventClasses = computed(() => ({
  deleted: props.event.deletedAt != null && archiveRange?.value === undefined,
}));

const currentBoardId = inject(BoardIdInjectionKey, ref<null>(null));
const currentIncludedFlagIds = inject(BoardIncludedFlagIdsInjectionKey, ref<null>(null));

function ifEventIncludedOverFlag(flag: EventListEventFieldsFragment["flags"][number]): boolean {
  /**
   * we return early when the event is displayed on the dashboard because of a false-positive result
   */
  if (currentBoardId.value == null) {
    return false;
  }
  return (
    !props.event.boardConnection.some((con) => con.boardId === currentBoardId.value) &&
    !!currentIncludedFlagIds.value?.includes(flag.id)
  );
}

const eventFilters = injectAsserted(EventFilterInjectionKey);

function setIdFilter() {
  if (props.event.linkedEvents.length > 0) {
    eventFilters.value = {
      updatedAt: DateTime.now(),
      eventFilters: [
        new IDFilter([...props.event.linkedEvents.map((event) => event.id), props.event.id]),
      ],
    };
  }
}
function setUserFilter(owner: EventListEventFieldsFragment["owner"], evt: MouseEvent) {
  if ((evt.ctrlKey || evt.metaKey) && owner != null) {
    eventFilters.value = {
      updatedAt: DateTime.now(),
      eventFilters: [new CreatorFilter([owner?.id])],
    };
  }
}
function setFlagFilter(flag: EventListEventFieldsFragment["flags"][number], evt: MouseEvent) {
  if (evt.ctrlKey || evt.metaKey) {
    eventFilters.value = {
      updatedAt: DateTime.now(),
      eventFilters: [new FlagsFilter([flag.id])],
    };
  }
}
const originBoardConnection = computed(() => {
  return props.event.boardConnection.find((con) => con.status === 0);
});

const { boardName, hasBoardAccess: hasOriginBoardAccess } = useGetBoardInfo(
  computed(() => originBoardConnection.value?.boardId),
);

const originBoard = computed(() => {
  if (originBoardConnection.value == null) {
    return undefined;
  }
  return {
    id: originBoardConnection.value.boardId,
    name: boardName.value,
  };
});

const buttonTitle = computed(() => {
  return `${t(
    "components.event-action.issue.AbstractIssue.originalBoard",
  ).toString()} "${originBoard?.value?.name}"`;
});
</script>

<style lang="scss" src="@/components/event-action/shared.scss"></style>

<style lang="scss" scoped>
.py-05 {
  padding-top: 0.125rem !important;
  padding-bottom: 0.125rem !important;
}

.event {
  color: var(--p-black) !important;
  .grow {
    display: flex;
    align-items: center;
  }
}

.IDBadgeWidth {
  min-width: calc(8ch + 10.5px);
}

.buttonGroup {
  flex-shrink: 0;

  :deep([data-lisa-popover-trigger]) {
    min-width: 2rem;
  }
}
</style>

<style lang="scss">
.buttonGroup {
  .btn-sm {
    font-size: 0.875em !important;
  }
}
.chipStyleEAB {
  font-size: 0.855em !important;
  padding: 0.5em !important;
  line-height: 1 !important;
  font-weight: normal !important;
}
</style>
